import { memo, useMemo } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { LIVE_STATUS_LABELS } from '@/constants/monitor-status'
import { useSemanticColors } from '@/constants/SemanticColors'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { useReverseGeocode } from '@/hooks/useReverseGeocode'
import { formatCompactDuration } from '@/lib/map-utils'
import { formatStartedLine } from '@/lib/monitor/formatStartedLine'
import { variantForStatus } from '@/lib/monitor/markerVariant'
import { getMovementLabel } from '@/lib/movement-utils'
import { formatSpeedMph } from '@/lib/units'
import { getNextWorkingPeriodStart } from '@/lib/working-hours'
import type { PathPoint, UserLocationWithStatus } from '@/types/movement'
import type { UserTrackingStatus } from '@/types/trackingStatus'
import { EntryTypeChip } from '@/ui/time-entry/EntryTypeChip'
import { LocationAuthIndicator } from './LocationAuthIndicator'
import { UserCardMiniMap } from './UserCardMiniMap'

interface Props {
    user: UserLocationWithStatus
    locationCircles: LocationCircle[]
    onPress: (userId: string) => void
}

function formatMinutesAgo(minutes: number | null): string {
    if (minutes === null) return ''
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    const rem = minutes % 60
    return rem > 0 ? `${hours}h ${rem}m ago` : `${hours}h ago`
}

function formatLeftSite(
    leftSiteAt: string | undefined,
    lastSiteJobName: string | undefined,
    now: Date
): string | null {
    if (!leftSiteAt || !lastSiteJobName) return null
    const minutes = Math.max(
        0,
        Math.floor((now.getTime() - new Date(leftSiteAt).getTime()) / 60_000)
    )
    return `Left ${lastSiteJobName} ${formatMinutesAgo(minutes)}`
}

function formatResumeTime(nextStart: Date | null): string {
    if (!nextStart) return 'No schedule'
    const now = new Date()
    const isToday = nextStart.toDateString() === now.toDateString()
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const isTomorrow = nextStart.toDateString() === tomorrow.toDateString()
    const timeStr = nextStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    if (isToday) return `Resumes today at ${timeStr}`
    if (isTomorrow) return `Resumes tomorrow at ${timeStr}`
    const dayName = nextStart.toLocaleDateString([], { weekday: 'long' })
    return `Resumes ${dayName} at ${timeStr}`
}

function getPrimaryText(
    status: UserTrackingStatus,
    mostRecentLocation: PathPoint | undefined
): string {
    switch (status.status) {
        case 'working_moving': {
            if (!mostRecentLocation) return LIVE_STATUS_LABELS.working_moving
            const label = getMovementLabel(
                mostRecentLocation.movement_type,
                mostRecentLocation.is_moving,
                mostRecentLocation.heading,
                mostRecentLocation.speed
            )
            const speed = formatSpeedMph(mostRecentLocation.speed)
            return label && label !== 'stationary' ? `${label} · ${speed}` : `Moving · ${speed}`
        }
        case 'working_on_site':
            return status.siteJobName
                ? `On site · ${status.siteJobName}`
                : LIVE_STATUS_LABELS.working_on_site
        case 'working_idle':
            return `Idle · ${formatMinutesAgo(status.minutesSinceLastUpdate)}`
        case 'working_untracked': {
            if (!mostRecentLocation) return LIVE_STATUS_LABELS.working_untracked
            const label = getMovementLabel(
                mostRecentLocation.movement_type,
                mostRecentLocation.is_moving,
                mostRecentLocation.heading,
                mostRecentLocation.speed
            )
            if (!label || label === 'stationary') return LIVE_STATUS_LABELS.working_untracked
            const speed = formatSpeedMph(mostRecentLocation.speed)
            return `${label} · ${speed}`
        }
        case 'after_hours_working':
            return LIVE_STATUS_LABELS.after_hours_working
        case 'before_hours_working':
            return LIVE_STATUS_LABELS.before_hours_working
        case 'off_hours':
            return LIVE_STATUS_LABELS.off_hours
        case 'not_tracking':
            return LIVE_STATUS_LABELS.not_tracking
        case 'no_data_today':
            return LIVE_STATUS_LABELS.no_data_today
        default: {
            const _exhaustive: never = status.status
            return _exhaustive
        }
    }
}

interface SecondaryText {
    text: string
    isTimeBased: boolean
}

interface SecondaryGeocode {
    placeName?: string | null
    address?: string | null
}

function getBreakLabel(onBreak: { clockIn: string } | undefined): string | null {
    if (!onBreak) return null
    const minutes = Math.max(
        0,
        Math.floor((Date.now() - new Date(onBreak.clockIn).getTime()) / 60_000)
    )
    return `On break · ${formatMinutesAgo(minutes).replace(' ago', '')}`
}

function shouldShowLastPing(
    status: UserTrackingStatus,
    secondary: { isTimeBased: boolean; text: string }
): boolean {
    if (!status.lastUpdate) return false
    if (status.status === 'working_on_site') return true
    return status.status === 'working_idle' && !secondary.isTimeBased && !secondary.text
}

function isMutedStatus(status: UserTrackingStatus['status']): boolean {
    return status === 'off_hours' || status === 'not_tracking' || status === 'no_data_today'
}

// When a user has tracked time earlier today but has no active entry now,
// they're most likely driving between jobs — show the vehicle icon so a
// manager can distinguish "off the clock for the day" (moon) from "off-site,
// expected to clock in on arrival" (car).
function getEntryIconType(dayEntry: UserLocationWithStatus['dayEntry']): string {
    if (dayEntry?.onBreak) return 'break'
    if (dayEntry?.currentEntry) return dayEntry.currentEntry.entryType
    if (dayEntry) return 'between_jobs'
    return 'none'
}

function SecondaryRow({
    secondaryText,
    lastUpdateText,
    mutedColor,
    outlineColor,
}: {
    secondaryText: string
    lastUpdateText: string | null
    mutedColor: string
    outlineColor: string
}) {
    if (!secondaryText && !lastUpdateText) return null
    return (
        <View style={styles.secondaryRow}>
            {secondaryText ? (
                <Text variant="bodySmall" numberOfLines={2} style={{ color: mutedColor }}>
                    {secondaryText}
                </Text>
            ) : null}
            {secondaryText && lastUpdateText ? (
                <Text variant="bodySmall" style={{ color: outlineColor }}>
                    ·
                </Text>
            ) : null}
            {lastUpdateText ? (
                <Text variant="bodySmall" style={{ color: outlineColor }}>
                    {lastUpdateText}
                </Text>
            ) : null}
        </View>
    )
}

function getSecondaryText(
    status: UserTrackingStatus,
    geocode: SecondaryGeocode | undefined
): SecondaryText {
    const now = new Date()
    const leftSite = formatLeftSite(status.leftSiteAt, status.lastSiteJobName, now)
    switch (status.status) {
        case 'working_on_site':
            return {
                text:
                    status.stationaryDurationMinutes != null
                        ? `On site for ${formatCompactDuration(status.stationaryDurationMinutes * 60)}`
                        : '',
                isTimeBased: false,
            }
        case 'working_moving':
            if (leftSite) return { text: leftSite, isTimeBased: true }
            return {
                text: formatMinutesAgo(status.minutesSinceLastUpdate),
                isTimeBased: true,
            }
        case 'working_idle':
            if (leftSite) return { text: leftSite, isTimeBased: true }
            if (geocode?.placeName) return { text: `Near ${geocode.placeName}`, isTimeBased: false }
            if (geocode?.address) return { text: `Near ${geocode.address}`, isTimeBased: false }
            return { text: '', isTimeBased: false }
        case 'working_untracked':
            if (leftSite) return { text: leftSite, isTimeBased: true }
            return {
                text: 'Within working hours but not clocked into a job',
                isTimeBased: false,
            }
        case 'after_hours_working':
            return { text: 'Past the scheduled end of shift', isTimeBased: false }
        case 'before_hours_working':
            return { text: 'Started before the scheduled shift', isTimeBased: false }
        case 'off_hours':
            return {
                text: formatResumeTime(getNextWorkingPeriodStart(status.workingHours)),
                isTimeBased: false,
            }
        case 'not_tracking':
            return { text: 'Geo tracking disabled', isTimeBased: false }
        case 'no_data_today':
            return { text: '', isTimeBased: false }
        default: {
            const _exhaustive: never = status.status
            return { text: _exhaustive, isTimeBased: false }
        }
    }
}

export const UserCard = memo(function UserCard({ user, locationCircles, onPress }: Props) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    const { trackingStatus: status, mostRecentLocation } = user

    const siteCircle = useMemo(() => {
        if (!status.siteJobId) return null
        return locationCircles.find(c => c.id === status.siteJobId) ?? null
    }, [status.siteJobId, locationCircles])

    const isIdle = status.status === 'working_idle'
    const { data: geocode } = useReverseGeocode({
        latitude: isIdle ? mostRecentLocation?.latitude : undefined,
        longitude: isIdle ? mostRecentLocation?.longitude : undefined,
        enabled: isIdle && mostRecentLocation != null,
    })

    const primary = useMemo(
        () => getPrimaryText(status, mostRecentLocation),
        [status, mostRecentLocation]
    )

    // Secondary returns both the rendered text and a flag indicating whether
    // that text already conveys "time since last update". When it does, the
    // tertiary "Last update HH:MM" line is suppressed to avoid repeating the
    // same information in two formats.
    const secondary = useMemo(() => getSecondaryText(status, geocode), [status, geocode])

    // Only show the "Last update HH:MM" line when it adds information not
    // already in the primary/secondary. That's the case for:
    //   - working_on_site (primary is place name, secondary is duration on site)
    //   - working_idle without leftSite context AND without a geocoded place
    //     (so we know WHEN they were last seen, since neither earlier line said)
    const showLastUpdate = shouldShowLastPing(status, secondary)
    const lastUpdateText =
        showLastUpdate && status.lastUpdate
            ? `Last update ${new Date(status.lastUpdate).toLocaleTimeString()}`
            : null

    const clockIn = useMemo(() => {
        const line = formatStartedLine(user.dayEntry)
        if (!line) return null
        // The on-site primary already names the place ("On site · {jobName}")
        // and the secondary already shows duration. The qualifier
        // "(at {jobName} since HH:MM)" repeats both — drop it.
        if (status.status === 'working_on_site' && line.qualifier) {
            return { primary: line.primary }
        }
        return line
    }, [user.dayEntry, status.status])

    // Intentionally not memoized: the break duration needs to recompute on
    // every render so it advances past "just now". UserCard is memo'd at
    // the user level and re-renders on the monitor's 60s nowTick (which
    // rebuilds each UserLocationWithStatus), so this runs ~once per minute.
    const breakLabel = getBreakLabel(user.dayEntry?.onBreak)

    const entryIconType = getEntryIconType(user.dayEntry)

    const markerVariant = useMemo(() => variantForStatus(status.status), [status.status])

    const isMuted = isMutedStatus(status.status)

    return (
        <Pressable
            onPress={() => onPress(user.userId)}
            style={({ pressed }) => [
                styles.card,
                {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.outlineVariant,
                    opacity: pressed ? 0.7 : isMuted ? 0.75 : 1,
                },
            ]}
        >
            <View style={styles.textColumn}>
                <View style={styles.header}>
                    <View style={[styles.colorChip, { backgroundColor: user.color }]} />
                    <Text
                        variant="titleMedium"
                        numberOfLines={1}
                        style={[styles.name, { color: theme.colors.onSurface }]}
                    >
                        {user.userName}
                    </Text>
                    <LocationAuthIndicator auth={user.locationAuth} />
                    <EntryTypeChip type={entryIconType} iconOnly />
                </View>

                {clockIn ? (
                    <View style={styles.clockInRow}>
                        <Text
                            variant="bodySmall"
                            style={[styles.clockIn, { color: semantic.successText }]}
                        >
                            {clockIn.primary}
                        </Text>
                        {clockIn.qualifier ? (
                            <Text
                                variant="bodySmall"
                                style={[styles.clockInQualifier, { color: semantic.successText }]}
                            >
                                {clockIn.qualifier}
                            </Text>
                        ) : null}
                    </View>
                ) : null}

                <Text
                    variant="bodyMedium"
                    numberOfLines={2}
                    style={[styles.primary, { color: theme.colors.onSurface }]}
                >
                    {primary}
                </Text>
                <SecondaryRow
                    secondaryText={secondary.text}
                    lastUpdateText={lastUpdateText}
                    mutedColor={semantic.mutedText}
                    outlineColor={theme.colors.outline}
                />
                {breakLabel ? (
                    <View
                        style={[
                            styles.breakChip,
                            {
                                backgroundColor: `${semantic.warning}22`,
                                borderColor: semantic.warning,
                            },
                        ]}
                    >
                        <Text
                            variant="labelSmall"
                            style={[styles.breakChipText, { color: semantic.warning }]}
                        >
                            {breakLabel}
                        </Text>
                    </View>
                ) : null}
            </View>

            {mostRecentLocation && markerVariant && status.status !== 'off_hours' ? (
                <UserCardMiniMap
                    location={mostRecentLocation}
                    userColor={user.color}
                    variant={markerVariant}
                    siteCircle={siteCircle}
                    width={120}
                    height={120}
                />
            ) : null}
        </Pressable>
    )
})

const styles = StyleSheet.create({
    card: {
        borderWidth: 1,
        borderRadius: 12,
        padding: 14,
        gap: 12,
        minHeight: 140,
        flex: 1,
        flexDirection: 'row',
        alignItems: 'stretch',
    },
    textColumn: {
        flex: 1,
        gap: 4,
        justifyContent: 'flex-start',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    colorChip: {
        width: 10,
        height: 24,
        borderRadius: 3,
    },
    name: {
        flex: 1,
        fontWeight: '600',
    },
    primary: {
        fontWeight: '500',
    },
    secondaryRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 6,
        rowGap: 2,
    },
    clockInRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 4,
        rowGap: 2,
    },
    clockIn: {
        fontWeight: '500',
    },
    clockInQualifier: {
        fontWeight: '400',
        opacity: 0.85,
    },
    breakChip: {
        alignSelf: 'flex-start',
        marginTop: 4,
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 12,
        borderWidth: 1,
    },
    breakChipText: {
        fontWeight: '600',
        fontSize: 11,
    },
})
